/** MentionsInbox — compact @-mentions surface for the Work primary.
 *  Shows every message the current user was tagged in, respecting visibility
 *  scope enforced by the server (`/api/conversations/inbox/mentions`).
 */
import { useEffect, useState } from "react";
import { AtSign, Loader2, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { ChatDrawer } from "@/components/app/ChatDrawer";

export default function MentionsInbox({ compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerConv, setDrawerConv] = useState(null);

  useEffect(() => {
    api.get("/conversations/inbox/mentions")
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="st-panel" data-testid="mentions-inbox">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--st-border)]">
        <AtSign className="h-4 w-4 text-[color:var(--st-accent)]" />
        <div className="text-[13px] font-semibold text-[color:var(--st-text)]">Mentions</div>
        <div className="text-[11px] text-[color:var(--st-text-muted)]">Messages that tag you</div>
        <div className="ml-auto text-[11px] text-[color:var(--st-text-subtle)]">{items.length}</div>
      </div>
      {loading && (
        <div className="p-6 text-center text-[12px] text-[color:var(--st-text-muted)]">
          <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading…
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="p-8 text-center text-[12px] text-[color:var(--st-text-muted)]">
          <MessageSquare className="mx-auto h-6 w-6 text-[color:var(--st-text-hint)]" />
          <div className="mt-2">You’re all caught up.</div>
        </div>
      )}
      {!loading && items.slice(0, compact ? 5 : items.length).map((m) => (
        <button
          key={m.id}
          onClick={() => setDrawerConv({ id: m.conversation_id })}
          data-testid={`mention-${m.id}`}
          className="w-full text-left px-4 py-3 border-b border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)]"
        >
          <div className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">
            @{m.author_name} · {new Date(m.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
          </div>
          <div className="text-[13px] text-[color:var(--st-text)] mt-0.5 line-clamp-2">{m.text}</div>
        </button>
      ))}
      {drawerConv && (
        <ChatDrawer onClose={() => setDrawerConv(null)} />
      )}
    </div>
  );
}
